import React, { ChangeEvent, isValidElement } from 'react';
import styled from 'styled-components/macro';
import { Flex, FlexProps } from '../styles/layout';
import { useField } from 'formik';
import { FormikLabel } from './FormikLabel';
import { isString } from 'lodash';
import { TOOLTIP_POSITION } from 'react-covideo-common/dist/components/Tooltip/types';

export type StyleObject = {
  wrapper?: React.CSSProperties;
  labelWrapper?: React.CSSProperties;
};

interface CustomRadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string | JSX.Element;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  wrapper?: FlexProps;
  info?: React.ReactNode;
  extendStyles?: StyleObject;
  tooltipPosition?: TOOLTIP_POSITION | undefined;
  readonly?: boolean;
}

const Input = styled.input<{
  disabled?: boolean;
  info?: string | React.ReactNode;
}>`
  position: absolute;
  opacity: 0;
  width: ${({ info }) => (info ? 'calc(100% - 20px)' : '100%')};
  height: 100%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  z-index: 1;
  margin: 0;
`;

const Circle = styled.label`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  z-index: 0;
  width: fit-content;
  min-height: 24px;
`;

const OuterCircle = styled.span<{ checked: boolean; disabled?: boolean }>`
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 24px;
  width: 24px;
  border: ${({ checked, theme }) =>
    checked
      ? `1px solid ${theme.colors.primary[100]}`
      : `1px solid ${theme.colors.black[20]}`};
  border-radius: 50%;
  background: ${({ checked, theme }) =>
    checked ? theme.colors.primary[100] : theme.colors.secondary[5]};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const InnerCircle = styled.span<{ checked: boolean; disabled?: boolean }>`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: ${({ checked, theme }) =>
    checked ? theme.colors.white[100] : `transparent`};
  border-radius: 50%;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;
const LabelWrapper = styled.div`
  margin-left: 30px;
`;

export const FormikRadioField = ({
  label,
  onChange,
  wrapper,
  info,
  extendStyles,
  tooltipPosition,
  readonly = false,
  ...props
}: CustomRadioProps) => {
  const [field, _, helpers] = useField(props);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) {
      return;
    }
    if (!!onChange) {
      onChange(e);
      return;
    }
    helpers.setValue(props.value);
  };

  const isChecked = String(props.value) === String(field.value);
  const inputId = `${props.name}-${props.value}`;
  return (
    <Flex
      {...wrapper}
      style={{
        position: 'relative',
        width: 'fit-content',
        ...extendStyles?.wrapper,
        pointerEvents: readonly ? 'none' : 'all',
      }}
    >
      <Input
        type='radio'
        {...props}
        {...field}
        onChange={handleChange}
        id={inputId}
        info={info}
      />
      <Circle htmlFor={inputId}>
        <OuterCircle checked={isChecked} disabled={props.disabled} />
        <InnerCircle checked={isChecked} disabled={props.disabled} />
        <LabelWrapper style={extendStyles?.labelWrapper}>
          {label && isString(label) && (
            <FormikLabel
              label={label}
              isRequired={false}
              disabled={!!props.disabled}
              info={info}
              tooltipPosition={tooltipPosition}
            />
          )}
          {label && isValidElement(label) && label}
        </LabelWrapper>
      </Circle>
    </Flex>
  );
};
